import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  alpha,
  TextField,
  Button,
  IconButton,
  Card,
  Stack,
  Icon,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Email,
  Phone,
  LocationOn,
  Facebook,
  LinkedIn,
  Instagram,
  Language,
  Send,
  AccessTime,
  Chat,
  Support,
} from "@mui/icons-material";

// Background Pattern Components
const GridPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: "64px 64px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const DotsPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 2px, transparent 2px)`,
      backgroundSize: "32px 32px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const CircleGlow = () => (
  <Box
    sx={{
      position: "absolute",
      width: "60%",
      paddingBottom: "60%",
      background:
        "radial-gradient(circle at center, rgba(6, 182, 212, 0.15) 0%, transparent 70%)",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 0,
      animation: "pulse 8s ease-in-out infinite",
      "@keyframes pulse": {
        "0%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
        "50%": {
          transform: "translate(-50%, -50%) scale(1.2)",
          opacity: 0.3,
        },
        "100%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
      },
    }}
  />
);

const ContactHero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        pt: { xs: 12, md: 16 },
        pb: { xs: 6, md: 10 },
        background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h1"
            align="center"
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
              fontWeight: 800,
              mb: 2,
              color: theme.palette.text.primary,
              lineHeight: 1.2,
              maxWidth: "800px",
              mx: "auto",
              fontFamily: "Montserrat",
            }}
          >
            Get in Touch
            <Box
              component="span"
              sx={{
                display: "block",
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                fontWeight: 600,
                mt: 1,
                fontFamily: "Montserrat",
                background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              We're Here to Help You Succeed
            </Box>
          </Typography>
          <Typography
            align="center"
            sx={{
              color: alpha(theme.palette.text.primary, 0.7),
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.6,
              maxWidth: "600px",
              mx: "auto",
              mb: { xs: 4, md: 6 },
              fontFamily: "Montserrat",
            }}
          >
            Have questions about our solutions? Our team is ready to assist you
            in transforming your restaurant operations.
          </Typography>
        </motion.div>
      </Container>
    </Box>
  );
};

const ContactForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Card
      elevation={0}
      sx={{
        background: "white",
        borderRadius: "24px",
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.1),
        p: { xs: 3, md: 4 },
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 1,
          fontFamily: "Montserrat",
          fontSize: { xs: "1.5rem", md: "1.75rem" },
          color: theme.palette.text.primary,
        }}
      >
        Send Us a Message
      </Typography>
      <Typography
        sx={{
          mb: 4,
          color: alpha(theme.palette.text.primary, 0.7),
          fontSize: "1rem",
          lineHeight: 1.6,
          fontFamily: "Montserrat",
        }}
      >
        Fill out the form below and we'll get back to you within 24 hours.
      </Typography>

      <Stack
        component="form"
        noValidate
        spacing={3}
        sx={{
          "& .MuiTextField-root": {
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              background: "#F8FAFC",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              "& fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.2),
                transition: "all 0.3s ease",
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              "&.Mui-focused": {
                background: "white",
                "& fieldset": {
                  borderWidth: "2px",
                  borderColor: theme.palette.primary.main,
                },
              },
            },
            "& .MuiInputLabel-root": {
              fontFamily: "Montserrat",
              fontSize: "0.9rem",
            },
            "& .MuiOutlinedInput-input": {
              fontFamily: "Montserrat",
              padding: "14px",
            },
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Full Name"
              placeholder="Enter your full name"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Email Address"
              type="email"
              placeholder="Enter your email address"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="Enter your phone number"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              multiline
              rows={4}
              label="Message"
              placeholder="Type your message here..."
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          size="large"
          endIcon={<Send />}
          sx={{
            py: 1.5,
            px: 4,
            background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
            borderRadius: "12px",
            textTransform: "none",
            fontSize: "1rem",
            fontFamily: "Montserrat",
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(6, 182, 212, 0.15)",
            "&:hover": {
              background: "linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(6, 182, 212, 0.25)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Send Message
        </Button>
      </Stack>
    </Card>
  );
};

const ContactInfo = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const contactItems = [
    {
      icon: <Email />,
      text: "support@venustusy.com",
      href: "mailto:support@venustusy.com",
      label: "Email Us",
      gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
    },
    {
      icon: <Phone />,
      text: "+973 362 00 663",
      href: "tel:+97336200663",
      label: "Call Us",
      gradient: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
    },
    {
      icon: <LocationOn />,
      text: "Bahrain, Manama, Exhibitions Avenue",
      href: "https://goo.gl/maps/...",
      label: "Visit Us",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    },
  ];

  const socialLinks = [
    {
      icon: <Facebook />,
      url: "https://www.facebook.com/people/Offical-Venustusy/",
      color: "#1877F2",
    },
    {
      icon: <LinkedIn />,
      url: "https://www.linkedin.com/company/venustusy-it/",
      color: "#0A66C2",
    },
    {
      icon: <Instagram />,
      url: "https://www.instagram.com/venustusy.t/#",
      color: "#E4405F",
    },
    { icon: <Language />, url: "https://venustusy.com/", color: "#06B6D4" },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        background: "white",
        borderRadius: "24px",
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.1),
        p: { xs: 3, md: 4 },
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: 3,
          fontFamily: "Montserrat",
          fontSize: { xs: "1.25rem", md: "1.5rem" },
          color: theme.palette.text.primary,
        }}
      >
        Contact Information
      </Typography>

      <Stack spacing={3}>
        {contactItems.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: item.gradient,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: `0 4px 12px ${alpha(
                    item.gradient.split(", ")[1].split(" ")[0],
                    0.2
                  )}`,
                }}
              >
                {item.icon}
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.7),
                    fontFamily: "Montserrat",
                    fontSize: "0.875rem",
                    mb: 0.5,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  component="a"
                  href={item.href}
                  sx={{
                    color: theme.palette.text.primary,
                    textDecoration: "none",
                    fontSize: "1rem",
                    fontWeight: 500,
                    fontFamily: "Montserrat",
                    "&:hover": {
                      color: item.gradient.split(", ")[1].split(" ")[0],
                    },
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        ))}

        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: alpha(theme.palette.text.primary, 0.7),
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              mb: 2,
            }}
          >
            Follow Us
          </Typography>
          <Stack direction="row" spacing={1.5}>
            {socialLinks.map((social, index) => (
              <IconButton
                key={index}
                component="a"
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: 36,
                  height: 36,
                  background: alpha(social.color, 0.1),
                  color: social.color,
                  "&:hover": {
                    background: alpha(social.color, 0.15),
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {social.icon}
              </IconButton>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};

const MapSection = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, background: "#F8FAFC" }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontFamily: "Montserrat",
              fontSize: { xs: "1.5rem", md: "2rem" },
              color: theme.palette.text.primary,
            }}
          >
            Visit Our Office
          </Typography>
          <Typography
            align="center"
            sx={{
              color: alpha(theme.palette.text.primary, 0.7),
              fontSize: "1rem",
              lineHeight: 1.6,
              maxWidth: "600px",
              mx: "auto",
              mb: 4,
              fontFamily: "Montserrat",
            }}
          >
            Come see us in person and let's discuss how we can help your
            business grow.
          </Typography>

          <Card
            elevation={0}
            sx={{
              height: { xs: "300px", md: "400px" },
              borderRadius: "24px",
              overflow: "hidden",
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d261.5386399184476!2d50.593206291499556!3d26.234804356370834!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2sbh!4v1744877849432!5m2!1sen!2sbh"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="VenFlows Location"
            />
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export const Contact: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={{ background: "white" }}>
      <ContactHero />

      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <ContactInfo />
            </Grid>
            <Grid item xs={12} md={7}>
              <ContactForm />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <MapSection />
    </Box>
  );
};
